import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Modal, Spinner, Alert, Row, Col } from 'react-bootstrap';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import config from '../../config';
import { toast } from 'react-toastify';
import VehicleForm from '../../components/master/VehicleForm';

const VehicleMaster = () => {
    const { transporterId } = useParams();
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const fetchVehicles = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${config.API_BASE_URL}/vehicles/by-transporter/${transporterId}`);
            setVehicles(res.data || []);
        } catch (err) {
            toast.error('Failed to fetch vehicles');
        } finally {
            setLoading(false);
        }
    }, [transporterId]);

    useEffect(() => {
        fetchVehicles();
    }, [fetchVehicles]);

    const handleSave = async (vehicleData) => {
        setLoading(true);
        try {
            if (vehicleData._id) {
                await axios.put(`${config.API_BASE_URL}/vehicles/${vehicleData._id}`, vehicleData);
                toast.success('Vehicle updated successfully');
            } else {
                await axios.post(`${config.API_BASE_URL}/vehicles`, { ...vehicleData, transporterId });
                toast.success('Vehicle created successfully');
            }
            fetchVehicles();
            setShowModal(false);
            setSelectedVehicle(null);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to save vehicle');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedVehicle) return;
        setLoading(true);
        try {
            await axios.delete(`${config.API_BASE_URL}/vehicles/${selectedVehicle._id}`);
            toast.success('Vehicle deactivated successfully');
            fetchVehicles();
            setShowDeleteConfirm(false);
            setSelectedVehicle(null);
        } catch (err) {
            toast.error('Failed to deactivate vehicle');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <Row className="mb-3">
                <Col>
                    <h2>Vehicle Management</h2>
                </Col>
                <Col className="text-end">
                    <Link to="/transporters" className="btn btn-secondary me-2">Back to Transporters</Link>
                    <Button variant="primary" onClick={() => { setSelectedVehicle(null); setShowModal(true); }}>
                        Add Vehicle
                    </Button>
                </Col>
            </Row>

            {loading && !vehicles.length ? (
                <Spinner animation="border" />
            ) : vehicles.length === 0 ? (
                <Alert variant="info">No vehicles found for this transporter.</Alert>
            ) : (
                <Table striped bordered hover responsive>
                    <thead>
                        <tr>
                            <th>Vehicle Number</th>
                            <th>Capacity (tons)</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {vehicles.map(v => (
                            <tr key={v._id}>
                                <td>{v.vehicleNumber}</td>
                                <td>{v.capacity}</td>
                                <td>
                                    <span className={`badge bg-${v.isActive ? 'success' : 'danger'}`}>
                                        {v.isActive ? 'Active' : 'Inactive'}
                                    </span>
                                </td>
                                <td>
                                    <Button variant="info" size="sm" onClick={() => { setSelectedVehicle(v); setShowModal(true); }} className="me-2">
                                        Edit
                                    </Button>
                                    {v.isActive && (
                                        <Button variant="danger" size="sm" onClick={() => { setSelectedVehicle(v); setShowDeleteConfirm(true); }}>
                                            Deactivate
                                        </Button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}

            <Modal show={showModal} onHide={() => { setShowModal(false); setSelectedVehicle(null); }}>
                <Modal.Header closeButton>
                    <Modal.Title>{selectedVehicle ? 'Edit' : 'Add'} Vehicle</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <VehicleForm
                        vehicle={selectedVehicle}
                        onSave={handleSave}
                        onCancel={() => { setShowModal(false); setSelectedVehicle(null); }}
                        loading={loading}
                    />
                </Modal.Body>
            </Modal>
            
            <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Deactivation</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to deactivate the vehicle "{selectedVehicle?.vehicleNumber}"?
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)} disabled={loading}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleDelete} disabled={loading}>
                        {loading ? 'Deactivating...' : 'Deactivate'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default VehicleMaster;
